// Die Leitung einer neuen Organisation aus einer Signatur (ST-PTY-001, Signatur
// S3, Vorgabe DESIGN V5-V7 und Abschnitt 5).
//
// WIE BEI DEN ANGABEN DER ORGANISATION AUF DEM REST (organisation-angaben.ts):
// free-text.ts legt "Geschäftsführer: Max Muster, Erika Probe" als ein Stueck
// in den Rest. Steht die Leitung in der Karte, nennt `taken` das Stueck, und
// der Bildschirm nimmt es aus "Nicht übernommen" (X6).
//
// ERKANNT HEISST VORGESCHLAGEN, NICHT ANGELEGT. Jede Person der Leitung steht
// als Haekchen da, standardmaessig aus (Entscheidung Dirigent): Personen ohne
// Kontaktweg nur aus einer Signatur anzulegen, ist Datensparsamkeit nur, wenn
// es jemand will. Gewaehlte Personen entstehen je mit einem eigenen Aufruf,
// `POST /api/parties` mit `leadsPartyId` (#786): Person und Kante "leitet"
// entstehen zusammen oder gar nicht.
//
// KEINE EIGENE FALTUNG. Zusammengefasst werden nur Namen, die nach dem Trimmen
// gleich geschrieben sind; der Abgleich mit einer vorhandenen Leitung (V8)
// gehoert dem Server (Entscheidung Dirigent 26.10.2026).

import { recognizedPersonName, type UnassignedFragment } from './free-text.ts';
import { artWort, type HausArt } from './haus-art.ts';
import { fetchRelationshipPage, type SendLike } from './relationships.ts';

/** Ein erkannter Name der Leitung; `anzeige` ist zugleich der Schluessel des Haekchens. */
export interface LeitungName {
  readonly anzeige: string;
  readonly givenName: string;
  readonly familyName: string;
}

export interface ErkannteLeitung {
  readonly namen: readonly LeitungName[];
  /** Das Reststueck, aus dem sie stammt, im Wortlaut. */
  readonly taken: readonly string[];
}

export const KEINE_ERKANNTE_LEITUNG: ErkannteLeitung = Object.freeze({ namen: Object.freeze([]), taken: Object.freeze([]) });

/** So viele Personen nimmt ein Vorschlag auf; darueber ist es eher eine Liste als eine Signatur. */
export const MAXIMALE_LEITUNG = 10;

// Nur am Anfang eines Stuecks und nur mit Doppelpunkt: "Geschäftsführer" allein ist eine Funktion.
const LEITUNG_LABEL =
  /^(?:Geschäftsführer(?:in|innen)?|Geschaeftsfuehrer(?:in|innen)?|Geschäftsführung|Geschäftsleitung|GF|Vorstand|Vorstände|Vorstandsmitglieder)\s*:\s*/iu;
const TRENNER = /\s*(?:,|;|&|\bund\b|\/)\s*/u;

function gleich(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Die Leitung aus dem Rest. Ein Stueck zaehlt nur ganz: laesst sich ein Teil
 * nicht als Name lesen, bleibt das ganze Stueck im Rest -- sonst verschwaende
 * ein Teil ungesehen. Die Person selbst faellt heraus, damit sie sich nicht
 * als ihre eigene Leitung vorschlaegt.
 */
export function leitungAusRest(unassigned: readonly UnassignedFragment[], selbst: { readonly givenName: string; readonly familyName: string }): ErkannteLeitung {
  const selbstName = `${selbst.givenName} ${selbst.familyName}`;
  for (const { text, reason } of unassigned) {
    if (reason !== 'not_recognized') continue;
    const ohneLabel = text.replace(LEITUNG_LABEL, '');
    if (ohneLabel === text) continue;
    const teile = ohneLabel.split(TRENNER).map((teil) => teil.trim()).filter((teil) => teil !== '');
    if (teile.length === 0 || teile.length > MAXIMALE_LEITUNG) return KEINE_ERKANNTE_LEITUNG;
    const namen: LeitungName[] = [];
    for (const teil of teile) {
      const name = recognizedPersonName(teil);
      if (name === undefined || name.givenName === '') return KEINE_ERKANNTE_LEITUNG;
      const anzeige = `${name.givenName} ${name.familyName}`;
      if (gleich(anzeige, selbstName) || namen.some((vorhanden) => vorhanden.anzeige === anzeige)) continue;
      namen.push({ anzeige, givenName: name.givenName, familyName: name.familyName });
    }
    // Bleibt niemand uebrig (nur die Person selbst), ist nichts uebernommen: das Stueck bleibt im Rest.
    return namen.length === 0 ? KEINE_ERKANNTE_LEITUNG : { namen, taken: [text] };
  }
  return KEINE_ERKANNTE_LEITUNG;
}

// --- Saetze (Vorgabe V5-V7, Abschnitt 5) ------------------------------------

/** Titel der Gruppe (legend). */
export function leitungTitel(anzahl: number, organisation: string): string {
  return `${anzahl === 1 ? '1 Person' : `${anzahl} Personen`} als Leitung von ${organisation} erkannt`;
}

export function leitungSatz(organisation: string): string {
  return `Wähle, wen LIAISON mit anlegen soll – als Entwurf, nur mit Namen und „leitet ${organisation}“.`;
}

/** Sichtbar unter der Gruppe (V6, Datensparsamkeit). */
export const LEITUNG_DATENSCHUTZ = 'Lege nur an, mit wem ihr arbeitet.';

/** Die Verbindungszeile der Vorschau (V7); `leads` traegt keine Funktion (O3). */
export function leitungVorschauVerbindung(organisation: string): string {
  return `leitet ${organisation}`;
}

export const LEITUNG_VORSCHAU_ZEILE = 'Person · Entwurf · ohne Kontaktweg';

/** "1 Person der Leitung" bzw. "2 Personen der Leitung". */
function personenDerLeitung(anzahl: number): string {
  return `${anzahl === 1 ? '1 Person' : `${anzahl} Personen`} der Leitung`;
}

/**
 * Die Hauptaktion mit gewaehlter Leitung: "Alle N+2 anlegen" (Person, neue
 * Organisation, N Personen). Nach einer Dublettenwarnung mit "Trotzdem" davor.
 */
export function leitungAnlegenLabel(gewaehlt: number, warningSeen: boolean, neueOrganisation = true): string {
  // Steht die Organisation schon (vorhanden oder nach dem Teilerfolg O4), entstehen nur Person und Leitung.
  const satz = `alle ${gewaehlt + (neueOrganisation ? 2 : 1)} anlegen`;
  return warningSeen ? `Trotzdem ${satz}` : `${satz.charAt(0).toUpperCase()}${satz.slice(1)}`;
}

export function leitungAngelegtTitel(person: string, organisation: string, anzahl: number): string {
  return `Angelegt: ${person}, ${organisation} und ${personenDerLeitung(anzahl)}`;
}

/** An einer vorhandenen Organisation (Abschnitt 5): die Organisation entsteht nicht mit. */
export function leitungAngelegtVorhandenTitel(person: string, organisation: string, anzahl: number): string {
  return `Angelegt: ${person} und ${personenDerLeitung(anzahl)} von ${organisation}`;
}

/** Zweite Zeile der Bestaetigung: dieselbe Hausregel wie bothSavedLine (M4). */
export function alleSichtbarZeile(houseName?: string, art?: HausArt | null): string {
  return `Alle als Entwurf, zunächst nur für ${houseName === undefined || houseName.length === 0 ? artWort(art, 'deinen') : houseName} sichtbar.`;
}

/**
 * Was schon steht, wenn ein Teil der Leitung fehlt. An einer vorhandenen
 * Organisation wurde sie nicht angelegt; der Satz sagt dann nur den Kontakt
 * (DESIGN zu #800).
 */
function schonGespeichert(organisationNeu: boolean): string {
  return organisationNeu ? 'Kontakt und Organisation sind angelegt.' : 'Der Kontakt ist angelegt.';
}

function namenListe(namen: readonly string[]): string {
  if (namen.length <= 1) return namen[0] ?? '';
  return `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`;
}

/**
 * Person und Organisation stehen, ein Teil der Leitung nicht. Der Satz nennt,
 * wer fehlt, und dass der zweite Versuch nur diese anlegt -- nie Person oder
 * Organisation noch einmal (dieselbe Lehre wie O4).
 */
export function leitungTeilerfolg(
  fehlend: readonly string[],
  organisation: string,
  offen: boolean,
  organisationNeu = true,
): { readonly headline: string; readonly explanation: string; readonly nextStep: string } {
  const liste = namenListe(fehlend);
  const gespeichert = schonGespeichert(organisationNeu);
  const mehrere = fehlend.length > 1;
  return offen
    ? {
        headline: `Ob ${liste} als Leitung von ${organisation} angelegt ${mehrere ? 'wurden' : 'wurde'}, ist offen.`,
        explanation: `Die Antwort kam nicht an. ${gespeichert}`,
        nextStep: `Wähle „Erneut versuchen“. Doppelt angelegt wird dabei nichts.`,
      }
    : {
        headline: `${liste} ${mehrere ? 'sind' : 'ist'} noch nicht als Leitung von ${organisation} angelegt.`,
        explanation: gespeichert,
        nextStep: `Wähle „Erneut versuchen“. Angelegt ${mehrere ? 'werden' : 'wird'} dann nur ${liste}.`,
      };
}

// --- Abgleich an einer vorhandenen Organisation (V8, #791) ------------------
//
// DER SERVER VERGLEICHT, NICHT DAS WEB. lead-matches faltet die Namen wie die
// Suche und sagt je Name "existing" (mit der Kennung der Leitung) oder "new" --
// ohne Namen (Zweckkatalog R-B8a). Die Namen der laufenden Leitung kommen aus
// dem Leseweg, gefiltert auf leads. "Nicht mehr" ist, wer dort steht und von
// keinem Namen getroffen wird. Beendet wird hier nichts: das bleibt eine
// Pflegeaktion mit Endgrund (Entscheidung Dirigent).

export interface LeitungAbgleich {
  /** Erkannte Namen, die eine laufende Leitung treffen; je Leitung einmal. */
  readonly schonDa: readonly string[];
  /** Erkannte Namen ohne Treffer: sie bekommen ein Haekchen wie in V5. */
  readonly neu: readonly LeitungName[];
  /** Laufende Leitung, die kein erkannter Name trifft (Anzeigename). */
  readonly nichtMehr: readonly string[];
}

export type LeitungAbgleichErgebnis =
  | { readonly outcome: 'loaded'; readonly abgleich: LeitungAbgleich }
  /** Unsichtbar, gibt es nicht oder keine Organisation: dann steht keine Gruppe da. */
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'failed' };

/** Mehr als so viele Seiten Leitung liest der Abgleich nicht; die Route liefert 50 je Seite. */
const MAXIMALE_SEITEN = 10;

export async function fetchLeitungAbgleich(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly organisationId: string;
  readonly namen: readonly LeitungName[];
  readonly send?: SendLike;
}): Promise<LeitungAbgleichErgebnis> {
  const send = options.send ?? fetch;
  const antwort = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.organisationId)}/lead-matches`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ names: options.namen.map((name) => name.anzeige) }),
  });
  if (antwort.status === 404) return { outcome: 'not-found' };
  if (!antwort.ok) return { outcome: 'failed' };
  const rumpf = ((await antwort.json().catch(() => undefined)) ?? {}) as Record<string, unknown>;
  const treffer = Array.isArray(rumpf['matches']) ? (rumpf['matches'] as readonly Record<string, unknown>[]) : [];
  if (treffer.length !== options.namen.length) return { outcome: 'failed' };

  const leitung: { readonly partyId: string; readonly name: string }[] = [];
  let cursor: string | undefined;
  for (let seite = 0; seite < MAXIMALE_SEITEN; seite += 1) {
    const ergebnis = await fetchRelationshipPage({
      baseUrl: options.baseUrl,
      accessToken: options.accessToken,
      partyId: options.organisationId,
      relationshipTypeCode: 'leads',
      ...(cursor === undefined ? {} : { cursor }),
      send,
    });
    if (ergebnis.outcome === 'not-found') return { outcome: 'not-found' };
    if (ergebnis.outcome !== 'loaded') return { outcome: 'failed' };
    for (const kante of ergebnis.relationships) {
      if (kante.relationshipTypeCode === 'leads') leitung.push({ partyId: kante.counterpartPartyId, name: kante.counterpartDisplayName });
    }
    cursor = ergebnis.nextCursor;
    if (cursor === undefined) break;
  }
  // Noch weitere Seiten: "nicht mehr" waere unvollstaendig, also kein Abgleich.
  if (cursor !== undefined) return { outcome: 'failed' };
  return { outcome: 'loaded', abgleich: leitungAbgleichAus(options.namen, treffer, leitung) };
}

/** Rein, damit die Zuordnung ohne Netz pruefbar ist. */
export function leitungAbgleichAus(
  namen: readonly LeitungName[],
  treffer: readonly Readonly<Record<string, unknown>>[],
  leitung: readonly { readonly partyId: string; readonly name: string }[],
): LeitungAbgleich {
  const getroffen = new Set<string>();
  const schonDa: string[] = [];
  const neu: LeitungName[] = [];
  namen.forEach((name, stelle) => {
    const eintrag = treffer[stelle];
    const partyId = typeof eintrag?.['partyId'] === 'string' ? eintrag['partyId'] : undefined;
    if (eintrag?.['status'] === 'existing' && partyId !== undefined) {
      // Zwei Schreibweisen derselben Leitung ("Müller", "Mueller") stehen einmal da (KERN zu #791).
      if (!getroffen.has(partyId)) schonDa.push(leitung.find((kante) => kante.partyId === partyId)?.name ?? name.anzeige);
      getroffen.add(partyId);
    } else {
      neu.push(name);
    }
  });
  const nichtMehr = [...new Set(leitung.filter((kante) => !getroffen.has(kante.partyId)).map((kante) => kante.name))];
  return { schonDa, neu, nichtMehr };
}

export const LEITUNG_ABGLEICH_TITEL = 'Leitung laut Signatur';
export const LEITUNG_ABGLEICH_GRENZE = 'Verglichen wird nur der Name.';

/**
 * Die Ansage, sobald das Ergebnis des Vergleichs steht (B16, Codex-P2 #800):
 * wer die Gruppe nicht sieht, erfaehrt so, dass es etwas zu waehlen gibt.
 * Teile mit 0 entfallen; ohne Neue und ohne Fehlende "alle leiten schon".
 */
export function leitungAbgleichAnsage(organisation: string, abgleich: LeitungAbgleich): string {
  const teile = [
    ...(abgleich.neu.length === 0 ? [] : [`${abgleich.neu.length} neu`]),
    ...(abgleich.schonDa.length === 0 ? [] : [`${abgleich.schonDa.length} ${abgleich.schonDa.length === 1 ? 'leitet' : 'leiten'} schon`]),
    ...(abgleich.nichtMehr.length === 0
      ? []
      : [`${abgleich.nichtMehr.length} ${abgleich.nichtMehr.length === 1 ? 'steht' : 'stehen'} nicht mehr in der Signatur`]),
  ];
  const ergebnis = abgleich.neu.length === 0 && abgleich.nichtMehr.length === 0 ? 'alle leiten schon' : teile.join(', ');
  return `Leitung von ${organisation} verglichen: ${ergebnis}.`;
}
export const LEITUNG_ABGLEICH_GESCHEITERT = 'Die Leitung ließ sich gerade nicht abgleichen. Der Kontakt lässt sich trotzdem anlegen.';

export function leitungSchonDa(name: string, organisation: string): string {
  return `${name} leitet ${organisation} schon.`;
}

export function leitungNichtMehr(name: string): string {
  return `${name} steht nicht mehr in der Signatur.`;
}

export function beziehungenAnsehen(organisation: string): string {
  return `Beziehungen von ${organisation} ansehen`;
}
/**
 * Endgueltig abgelehnt (DESIGN B15): die Organisation nimmt gerade keine
 * Leitung an (422 an leadsPartyId, 409 counterpart_not_writable). Ein zweiter
 * Versuch aendert daran nichts, deshalb gibt es keinen Wiederholknopf; der
 * Satz nennt den Weg, der bleibt.
 */
export function leitungEndgueltigAbgelehnt(
  namen: readonly string[],
  organisation: string,
  organisationNeu = true,
): { readonly headline: string; readonly explanation: string; readonly nextStep: string } {
  return {
    headline: `${namenListe(namen)} ${namen.length > 1 ? 'wurden' : 'wurde'} nicht angelegt.`,
    explanation: `Bei ${organisation} lässt sich gerade keine Leitung eintragen. ${schonGespeichert(organisationNeu)}`,
    nextStep: 'Lege die Person bei Bedarf später einzeln an. Klappt das nicht, wende dich an die Datenpflege.',
  };
}

/** Die Antworten, nach denen ein zweiter Versuch nichts aendert (B15). */
export function istEndgueltigAbgelehnt(ergebnis: {
  readonly kind: string;
  readonly reasonCode?: string | undefined;
  readonly errors?: readonly { readonly fieldPath: string; readonly reasonCode: string }[];
}): boolean {
  if (ergebnis.kind === 'rejected') return ergebnis.reasonCode === 'counterpart_not_writable';
  if (ergebnis.kind === 'invalid') return (ergebnis.errors ?? []).some((fehler) => fehler.fieldPath === 'leadsPartyId' && fehler.reasonCode === 'field_value_unsupported');
  return false;
}

